import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronLeft, Users, Phone, Mail, Calendar, CheckCircle, 
  XCircle, Clock, MessageSquare, Loader2, Search, Filter,
  PhoneCall, PhoneOff, UserCheck, UserX, ExternalLink,
  CalendarCheck, FileText, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const statusConfig = {
  new: { 
    label: 'Nouveau', 
    labelEn: 'New',
    labelNl: 'Nieuw',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    icon: Clock 
  },
  called: { 
    label: 'Appelé', 
    labelEn: 'Called',
    labelNl: 'Gebeld',
    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    icon: PhoneCall 
  },
  qualified: { 
    label: 'Qualifié', 
    labelEn: 'Qualified',
    labelNl: 'Gekwalificeerd',
    color: 'bg-green-500/20 text-green-400 border-green-500/30',
    icon: UserCheck 
  },
  converted: { 
    label: 'Converti', 
    labelEn: 'Converted',
    labelNl: 'Geconverteerd',
    color: 'bg-primary/20 text-primary border-primary/30',
    icon: CheckCircle 
  },
  rejected: { 
    label: 'Rejeté', 
    labelEn: 'Rejected',
    labelNl: 'Afgewezen',
    color: 'bg-red-500/20 text-red-400 border-red-500/30',
    icon: XCircle 
  }
};

export default function AdminCoachLeadsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, lang } = useTranslation();
  
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ new: 0, called: 0, qualified: 0, converted: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchLeads = async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/coach-leads`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Calculate stats
        const newStats = {
          new: data.filter(l => l.status === 'new').length,
          called: data.filter(l => l.status === 'called').length,
          qualified: data.filter(l => l.status === 'qualified').length,
          converted: data.filter(l => l.status === 'converted').length,
          rejected: data.filter(l => l.status === 'rejected').length,
          total: data.length
        };
        setStats(newStats);
        
        // Apply filter
        let filteredData = data;
        if (filter !== 'all') {
          filteredData = data.filter(l => l.status === filter);
        }
        
        // Apply search
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          filteredData = filteredData.filter(l => 
            l.first_name?.toLowerCase().includes(query) ||
            l.last_name?.toLowerCase().includes(query) ||
            l.email?.toLowerCase().includes(query) ||
            l.phone?.includes(query)
          );
        }
        
        setLeads(filteredData);
      } else if (response.status === 401 || response.status === 403) {
        toast.error(t('admin.toastUnauthorized') || 'Accès non autorisé');
        navigate('/login');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [isAuthenticated, filter]);

  // Check authentication - must be after all hooks
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-xl p-8 max-w-md text-center">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('admin.restrictedAccess')}</h2>
          <p className="text-muted-foreground mb-6">{t('admin.loginToAccess')}</p>
          <Button onClick={() => navigate('/login')} className="btn-primary">
            {t('admin.login')}
          </Button>
        </div>
      </div>
    );
  }

  const updateLeadStatus = async (leadId, newStatus) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/coach-leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Lead marqué comme "${getStatusLabel(newStatus)}"`);
        fetchLeads();
        if (selectedLead?.lead_id === leadId) {
          setSelectedLead(prev => ({ ...prev, status: newStatus }));
        }
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setProcessing(false);
    }
  };

  const updateLeadNotes = async (leadId) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/coach-leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes })
      });
      
      if (response.ok) {
        toast.success('Notes enregistrées');
        fetchLeads();
        if (selectedLead?.lead_id === leadId) {
          setSelectedLead(prev => ({ ...prev, notes }));
        }
      } else {
        toast.error('Erreur lors de la sauvegarde des notes');
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setProcessing(false);
    }
  };

  const toggleCallScheduled = async (leadId, currentValue) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/coach-leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ call_scheduled: !currentValue })
      });
      
      if (response.ok) {
        toast.success(!currentValue ? 'Appel programmé' : 'Appel déprogrammé');
        fetchLeads();
        if (selectedLead?.lead_id === leadId) {
          setSelectedLead(prev => ({ ...prev, call_scheduled: !currentValue }));
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const toggleCallCompleted = async (leadId, currentValue) => {
    setProcessing(true);
    try {
      const updates = { call_completed: !currentValue };
      // If marking as completed, also update status to "called"
      if (!currentValue) {
        updates.status = 'called';
      }
      
      const response = await fetch(`${API_URL}/api/coach-leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        toast.success(!currentValue ? 'Appel marqué comme effectué' : 'Appel marqué comme non effectué');
        fetchLeads();
        if (selectedLead?.lead_id === leadId) {
          setSelectedLead(prev => ({ ...prev, call_completed: !currentValue, status: !currentValue ? 'called' : prev.status }));
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusLabel = (status) => {
    const config = statusConfig[status];
    if (!config) return status;
    if (lang === 'en') return config.labelEn;
    if (lang === 'nl') return config.labelNl;
    return config.label;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'nl' ? 'nl-NL' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openLeadDetails = (lead) => {
    setSelectedLead(lead);
    setNotes(lead.notes || '');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Leads Coach
                </h1>
                <p className="text-sm text-muted-foreground">
                  Gérez les demandes de coachs potentiels
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchLeads}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">
                Dashboard Admin
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`p-4 rounded-xl border transition-all ${
              filter === 'all' 
                ? 'bg-primary/20 border-primary/50 ring-2 ring-primary/30' 
                : 'bg-card border-border hover:border-primary/30'
            }`}
            data-testid="filter-all"
          >
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </button>
          
          {Object.entries(statusConfig).map(([key, config]) => {
            const IconComponent = config.icon;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`p-4 rounded-xl border transition-all ${
                  filter === key 
                    ? `${config.color} ring-2 ring-offset-2 ring-offset-background` 
                    : 'bg-card border-border hover:border-primary/30'
                }`}
                data-testid={`filter-${key}`}
              >
                <div className="flex items-center gap-2">
                  <IconComponent className="w-4 h-4" />
                  <span className="text-2xl font-bold">{stats[key]}</span>
                </div>
                <div className="text-sm text-muted-foreground">{getStatusLabel(key)}</div>
              </button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                // Debounce search
                setTimeout(fetchLeads, 300);
              }}
              className="pl-11"
              data-testid="search-leads"
            />
          </div>
        </div>

        {/* Leads List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Aucun lead trouvé</h3>
            <p className="text-muted-foreground">
              {filter !== 'all' 
                ? `Aucun lead avec le statut "${getStatusLabel(filter)}"`
                : 'Les leads apparaîtront ici quand des coachs rempliront le formulaire'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => {
              const config = statusConfig[lead.status] || statusConfig.new;
              const IconComponent = config.icon;
              
              return (
                <div
                  key={lead.lead_id}
                  className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-all cursor-pointer"
                  onClick={() => openLeadDetails(lead)}
                  data-testid={`lead-card-${lead.lead_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg">
                          {lead.first_name} {lead.last_name}
                        </h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
                          <IconComponent className="w-3 h-3" />
                          {getStatusLabel(lead.status)}
                        </span>
                        {lead.call_scheduled && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            <CalendarCheck className="w-3 h-3" />
                            Appel programmé
                          </span>
                        )}
                        {lead.call_completed && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                            <PhoneCall className="w-3 h-3" />
                            Appelé
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <a 
                          href={`mailto:${lead.email}`} 
                          className="flex items-center gap-1 hover:text-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Mail className="w-4 h-4" />
                          {lead.email}
                        </a>
                        <a 
                          href={`tel:${lead.phone}`} 
                          className="flex items-center gap-1 hover:text-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone className="w-4 h-4" />
                          {lead.phone}
                        </a>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(lead.created_at)}
                        </span>
                      </div>
                      
                      {lead.notes && (
                        <div className="mt-2 text-sm text-muted-foreground bg-muted/30 rounded-lg p-2">
                          <MessageSquare className="w-4 h-4 inline mr-1" />
                          {lead.notes.length > 100 ? lead.notes.slice(0, 100) + '...' : lead.notes}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openLeadDetails(lead);
                        }}
                      >
                        Détails
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lead Details Modal */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
          <div 
            className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-details-modal"
          >
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {selectedLead.first_name} {selectedLead.last_name}
              </h2>
              <button 
                onClick={() => setSelectedLead(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="text-sm text-muted-foreground mb-1">Email</div>
                  <a href={`mailto:${selectedLead.email}`} className="font-medium hover:text-primary flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {selectedLead.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="text-sm text-muted-foreground mb-1">Téléphone</div>
                  <a href={`tel:${selectedLead.phone}`} className="font-medium hover:text-primary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {selectedLead.phone}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

              {/* Status Update */}
              <div>
                <label className="block text-sm font-medium mb-3">Statut du lead</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusConfig).map(([key, config]) => {
                    const IconComponent = config.icon;
                    const isActive = selectedLead.status === key;
                    return (
                      <button
                        key={key}
                        onClick={() => updateLeadStatus(selectedLead.lead_id, key)}
                        disabled={processing}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          isActive 
                            ? `${config.color} ring-2 ring-offset-2 ring-offset-background` 
                            : 'bg-muted/30 border-border hover:border-primary/30'
                        }`}
                        data-testid={`status-btn-${key}`}
                      >
                        <IconComponent className="w-4 h-4" />
                        {getStatusLabel(key)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Call Tracking */}
              <div>
                <label className="block text-sm font-medium mb-3">Suivi d'appel</label>
                <div className="flex gap-4">
                  <button
                    onClick={() => toggleCallScheduled(selectedLead.lead_id, selectedLead.call_scheduled)}
                    disabled={processing}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                      selectedLead.call_scheduled 
                        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
                        : 'bg-muted/30 border-border hover:border-yellow-500/30'
                    }`}
                    data-testid="toggle-call-scheduled"
                  >
                    <CalendarCheck className="w-5 h-5" />
                    {selectedLead.call_scheduled ? 'Appel programmé ✓' : 'Programmer appel'}
                  </button>
                  <button
                    onClick={() => toggleCallCompleted(selectedLead.lead_id, selectedLead.call_completed)}
                    disabled={processing}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                      selectedLead.call_completed 
                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                        : 'bg-muted/30 border-border hover:border-green-500/30'
                    }`}
                    data-testid="toggle-call-completed"
                  >
                    <PhoneCall className="w-5 h-5" />
                    {selectedLead.call_completed ? 'Appel effectué ✓' : 'Marquer appelé'}
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">Notes internes</label>
                <Textarea
                  placeholder="Ajoutez des notes après l'appel de validation..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[120px]"
                  data-testid="lead-notes"
                />
                <Button
                  onClick={() => updateLeadNotes(selectedLead.lead_id)}
                  disabled={processing || notes === (selectedLead.notes || '')}
                  className="mt-3"
                  data-testid="save-notes-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  Enregistrer les notes
                </Button>
              </div>

              {/* Metadata */}
              <div className="pt-4 border-t border-border">
                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                  <div>
                    <span className="font-medium">Créé le:</span> {formatDate(selectedLead.created_at)}
                  </div>
                  <div>
                    <span className="font-medium">Modifié le:</span> {formatDate(selectedLead.updated_at) || '-'}
                  </div>
                  <div>
                    <span className="font-medium">Source:</span> {selectedLead.source}
                  </div>
                  <div>
                    <span className="font-medium">ID:</span> {selectedLead.lead_id}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(`mailto:${selectedLead.email}`, '_blank')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer un email
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(`tel:${selectedLead.phone}`, '_blank')}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Appeler
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
